import React from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import "./App.scss";
import AppLayout from "./components/Layouts/AppLayout/AppLayout";
import Loading from "./components/Loading/Loading";
import ContactUsModal from "./components/Modals/DrawerModals/ContactUsModal/ContactUsModal";
import FaqModal from "./components/Modals/DrawerModals/FaqModal/FaqModal";
import HowToPlayModal from "./components/Modals/DrawerModals/HowToPlayModal/HowToPlayModal";
import HomePage from "./containers/HomePage/HomePage";
import LandingPage from "./containers/LandingPage/LandingPage";
import QuestionPage from "./containers/QuestionPage/QuestionPage";
import { apiErrorSet } from "./core/app/apiError/actions";
import { setMe } from "./core/app/setMe/actions";
import { setPathname } from "./core/app/setPathname/actions";
import { ErrorDto, getErrorDtoFromApiError } from "./core/models/dtos/error.dto";
import { MeDto } from "./core/models/dtos/me.dto";
import { LoginRequest } from "./core/models/requests/login.request";
import { login } from "./core/services/authService/login/actions";
import { LoginState } from "./core/services/authService/login/types";
import { getContents } from "./core/services/contentStringService/getContents/actions";
import { GetContentsState } from "./core/services/contentStringService/getContents/types";
import FirebaseService from "./core/services/firebase.service";
import { getMe } from "./core/services/userService/getMe/actions";
import { GetMeState } from "./core/services/userService/getMe/types";
import axios from "./core/utilities/axios";
import { Errors } from "./core/utilities/errors";
import { Helpers } from "./core/utilities/helpers";
import { withRouter, WithRouterProps } from "./core/utilities/hooks";
import { IStore } from "./core/utilities/reducers";
import { router } from "./core/utilities/router";

interface IProps {
  me?: MeDto;
  pathname: string;
  getContentsState: GetContentsState;
  getMeState: GetMeState;
  loginState: LoginState;
  setMe: (me?: MeDto) => void;
  setPathname: (pathname: string) => void;
  apiErrorSet: (error: ErrorDto) => void;
  getContents: () => void;
  getMe: () => void;
  login: (request: LoginRequest) => void;
}

interface IState {
  callbackHistoryUnregister?: VoidFunction;
}

class App extends React.Component<WithRouterProps<IProps>> {
  state: IState = {
    callbackHistoryUnregister: undefined,
  }

  constructor(props: WithRouterProps<IProps>) {
    super(props);
    this.setAxiosInterceptor();
    this.props.setMe();
    this.props.getContents();
    const walletToken = Helpers.getQueryParam(this.props.location.search, 'token');
    if (!walletToken) {
      alert('Tüh! Beklenmeyen bir sorun oluştu.\nTekrar dene');
      return;
    }
    this.props.login({ walletToken });
  }

  componentDidUpdate(prevProps: Readonly<WithRouterProps<IProps>>, prevState: Readonly<{}>, snapshot?: any) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.props.setPathname(this.props.location.pathname);
    }
  }

  private setAxiosInterceptor(): void {
    axios.interceptors.response.use(
      response => {
        return response;
      },
      async error => {
        // response body empty in case of 401 (leads to generic error)
        // handle it via status check
        const isAuthErrorStatus = Errors.isAuthErrorStatus(error);
        const errorDto = getErrorDtoFromApiError(error);
        const isAuthError = Errors.isAuthError(errorDto);
        const isUnauthenticated = isAuthErrorStatus || isAuthError;
        if (isUnauthenticated || Errors.isDeviceError(errorDto)) {
          this.props.setMe();
        }
        if (!isUnauthenticated) {
          this.props.apiErrorSet(errorDto);
          if (errorDto.message === Errors.errorMessagePaymentCountExceeded) {
            FirebaseService.logEvent(FirebaseService.user_count_kmb_game_limit_exceeded);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private isAppLoading(): boolean {
    return (
      this.props.getContentsState.loading ||
      this.props.loginState.loading ||
      (!this.props.me && this.props.getMeState.loading)
    );
  }

  private isAppVisible(): boolean {
    return !!this.props.me;
  }

  render() {
    return (
      this.isAppLoading()
        ?
        <AppLayout>
          <Loading className="app-loading" size="large" />
        </AppLayout>
        :
        <>
          {this.isAppVisible()
            ?
            <AppLayout>
              <Routes>
                <Route path={router.LANDING} element={<LandingPage />} />
                <Route path={router.HOME} element={<HomePage />} />
                <Route path={router.QUESTION} element={<QuestionPage />} />
                <Route
                  path={router.GIFT_STORE}
                  element={<Loading className="app-loading" size="large" />}
                />
                <Route path="*" element={<Navigate to={router.LANDING} />} />
              </Routes>
            </AppLayout>
            :
            <AppLayout>
              <Routes>
                <Route
                  path={router.LANDING}
                  element={<span className="unauthorized">Token not defined!</span>}
                />
                <Route path="*" element={<Navigate to={router.LANDING} />} />
              </Routes>
            </AppLayout>
          }
          <HowToPlayModal />
          <FaqModal />
          <ContactUsModal />
        </>
    );
  }

  componentWillUnmount() {
    if (this.state.callbackHistoryUnregister) {
      this.state.callbackHistoryUnregister();
    }
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators(
    {
      setMe,
      setPathname,
      apiErrorSet,
      getContents,
      getMe,
      login,
    },
    dispatch
  );
};
const mapStateToProps = (store: IStore) => {
  return {
    me: store.setMe.me,
    pathname: store.setPathname.pathname,
    getContentsState: store.getContents,
    getMeState: store.getMe,
    loginState: store.login,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter<IProps>(App));
